import {
  Layers,
  Zap,
  Check,
  ArrowUp,
  File,
  ChevronRight,
  ChevronDown,
  MessageSquare,
  FolderOpen,
  Folder,
  Loader2,
  Terminal,
  Cpu,
} from "lucide-react";
import { useState, useRef, useEffect } from "react";

export function Mockup() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const vh = window.innerHeight;
      
      if (rect.top > vh * 0.6) {
        setPhase(0); 
      } else if (rect.top > vh * 0.25) {
        setPhase(1); 
      } else {
        setPhase(2); 
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll(); 
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div 
      ref={containerRef}
      className="w-full max-w-[920px] rounded-xl overflow-hidden border border-white/[0.08] bg-[#09090b] shadow-[0_24px_60px_rgba(0,0,0,0.8),0_0_120px_-20px_rgba(255,255,255,0.03)] transition-all duration-500"
    >
      {/* Sleek Header */}
      <div className="flex items-center h-10 bg-[#09090b] border-b border-white/[0.05] px-4 gap-3">
        <div className="flex gap-1.5 mr-2">
          <div className="w-2.5 h-2.5 rounded-full bg-white/[0.08]" />
          <div className="w-2.5 h-2.5 rounded-full bg-white/[0.08]" />
          <div className="w-2.5 h-2.5 rounded-full bg-white/[0.08]" />
        </div>
        
        <div className="py-1 px-3 bg-[#0a0a0c] border border-white/[0.03] rounded-md text-[11px] text-white/50 flex items-center gap-2 font-mono tracking-wide">
          <Layers size={11} className="text-white/30" /> my-app
        </div>
        <div className="py-1 px-2 text-[11px] text-white/20 font-mono tracking-wider uppercase">
          API Server
        </div>
      </div>

      <div className="flex h-[400px]">
        {/* Minimal Activity bar */}
        <div className="w-12 bg-[#09090b] border-r border-white/[0.05] flex flex-col items-center pt-3 gap-2">
          {[true, false].map((active, i) => (
            <div
              key={i}
              className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors duration-300 ${
                active
                  ? "bg-white/[0.05] text-white/80"
                  : "text-white/20 hover:text-white/50"
              }`}
            >
              {i === 0 ? (
                <MessageSquare size={14} />
              ) : (
                <Terminal size={14} />
              )}
            </div>
          ))}
        </div>

        {/* Sidebar */}
        <div className="w-[160px] bg-[#0a0a0c] border-r border-white/[0.03] p-3 text-[11px] font-mono text-white/40">
          <div className="text-[10px] text-white/20 uppercase tracking-[0.1em] px-1 pb-3 font-medium">
            Explorer
          </div>
          {[
            { name: "src", indent: 0, isFolder: true, open: true },
            { name: "components", indent: 1, isFolder: true, open: false },
            { name: "SignupForm.tsx", indent: 2, active: phase === 0 },
            { name: "lib", indent: 1, isFolder: true, open: true },
            ...(phase >= 1 ? [{ name: "validation.ts", indent: 2, active: phase >= 1, isNew: phase === 1 }] : []),
            ...(phase >= 1 ? [{ name: "auth.ts", indent: 2 }] : []),
            { name: "app", indent: 1, isFolder: true, open: false },
            { name: "page.tsx", indent: 2 },
          ].map((f, i) => (
            <div
              key={f.name}
              className={`py-1 px-1 flex items-center gap-1 transition-all duration-300 ${
                f.active ? "text-white/90 bg-white/[0.03] rounded" : "text-white/40"
              } ${f.isNew ? "opacity-70 animate-pulse" : "opacity-100"}`}
              style={{ paddingLeft: `${4 + f.indent * 12}px` }}
            >
              {f.isFolder ? (
                <>
                  {f.open ? (
                    <ChevronDown size={11} className="shrink-0 opacity-40" />
                  ) : (
                    <ChevronRight size={11} className="shrink-0 opacity-40" />
                  )}
                  {f.open ? (
                    <FolderOpen size={11} className="shrink-0 opacity-30 mr-1" />
                  ) : (
                    <Folder size={11} className="shrink-0 opacity-30 mr-1" />
                  )}
                </>
              ) : (
                <span className="w-2.5 shrink-0" />
              )}
              {f.name}
            </div>
          ))}
        </div>

        {/* Chat panel */}
        <div className="flex-[0_0_42%] flex flex-col border-r border-white/[0.05] bg-[#09090b]">
          <div className="py-2.5 px-4 border-b border-white/[0.03] text-[11px] font-mono tracking-wide text-white/40">
            Current Task: Signup Validation
          </div>
          <div className="flex-1 flex flex-col justify-end pb-2 overflow-hidden">
            <div className="flex flex-col justify-end h-full">
              
              {/* User message */}
              <div className="px-4 py-3">
                <div className="flex items-center gap-2 mb-1.5">
                  <div className="w-5 h-5 rounded border border-white/10 flex items-center justify-center text-[9px] text-white/60 font-medium">
                    Y
                  </div>
                  <span className="text-xs font-medium text-white/70">
                    You
                  </span>
                </div>
                <div className="text-[13px] text-white/40 pl-7 leading-relaxed">
                  Add input validation to the signup form.
                </div>
              </div>

              {/* Architect message */}
              <div 
                className={`px-4 py-3 transition-all duration-700 transform origin-top ${
                  phase >= 1 ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-4 scale-95 h-0 overflow-hidden py-0"
                }`}
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <div className="w-5 h-5 rounded bg-white/[0.03] border border-white/10 flex items-center justify-center text-white/80">
                    <Cpu size={10} />
                  </div>
                  <span className="text-xs font-medium text-white/80">
                    Architect
                  </span>
                </div>
                <div className="pl-7">
                  <div className="text-[13px] text-white/40 leading-relaxed mb-2">
                    Delegating sub-tasks to available agents.
                  </div>
                  <div className="rounded-md border border-white/[0.05] bg-[#0a0a0c] p-2.5 text-[11px] font-mono">
                    <div className="flex items-center gap-2">
                      {phase === 1 ? (
                        <Loader2 size={12} className="text-white/40 animate-spin" />
                      ) : (
                        <div className="w-1.5 h-1.5 rounded-full bg-white/40" />
                      )}
                      <span className="text-white/30">Assigned </span>
                      <span className="text-white/70">
                        agent-1.ts
                      </span>
                      {phase >= 2 && (
                        <span className="ml-auto text-white/50 flex items-center gap-1.5 animate-[fade-in_0.5s]">
                          <Check size={11} className="text-white/40" /> Done
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Review message */}
              <div 
                className={`px-4 py-3 transition-all duration-700 transform origin-top delay-100 ${
                  phase >= 2 ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-4 scale-95 h-0 overflow-hidden py-0"
                }`}
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <div className="w-5 h-5 rounded bg-white/[0.03] border border-white/10 flex items-center justify-center text-white/80">
                    <Cpu size={10} />
                  </div>
                  <span className="text-xs font-medium text-white/80">
                    Architect
                  </span>
                </div>
                <div className="pl-7">
                  <div className="text-[13px] text-white/40 leading-relaxed mb-2">
                    Task completed successfully.
                  </div>
                  <div className="inline-flex items-center gap-1.5 py-1 px-2.5 rounded border border-white/[0.08] bg-white/[0.02] text-[11px] font-mono text-white/60">
                    <Check size={11} className="opacity-70" /> 2 files modified
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Minimal Chat input */}
          <div className="p-3">
            <div className="bg-[#0a0a0c] border border-white/[0.05] rounded-lg p-2.5 flex items-center justify-between">
              <span className="text-xs text-white/20 font-sans tracking-wide">
                Message...
              </span>
              <div className="w-6 h-6 rounded bg-white/[0.05] border border-white/[0.05] flex items-center justify-center">
                <ArrowUp size={11} className="text-white/40" />
              </div>
            </div>
          </div>
        </div>

        {/* Code viewer */}
        <div className="flex-1 flex flex-col bg-[#09090b]">
          {/* Minimal URL bar */}
          <div className="flex items-center py-2.5 px-3 border-b border-white/[0.03] bg-[#09090b]">
            <div className={`flex-1 rounded py-1 px-2 text-[10px] font-mono flex items-center gap-2 ${phase > 0 ? "text-white/50" : "text-white/30"}`}>
              {phase > 0 && <File size={10} className="opacity-50" />} 
              {phase > 0 ? "workspace/src/lib/validation.ts" : "workspace/src/components/SignupForm.tsx"}
            </div>
          </div>

          {/* Premium Code text */}
          <div className="flex-1 py-3 text-[12px] font-mono leading-loose text-white/30 relative overflow-hidden bg-[#09090b]">
            <div className={`absolute inset-0 transition-opacity duration-700 ${phase === 0 ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
              {[
                "// SignupForm.tsx",
                "",
                "export function SignupForm() {",
                "  const [email, setEmail] = useState('');",
                "  const [pwd, setPwd] = useState('');",
                "",
                "  return (",
                "    <form>",
                "      <input type=\"email\" />",
                "      <input type=\"password\" />",
                "      <button>Sign up</button>",
                "    </form>",
                "  );",
                "}",
              ].map((line, i) => (
                <div key={i} className="flex px-4">
                  <span className="w-8 text-right pr-4 text-white/10 select-none">
                    {i + 1}
                  </span>
                  <span className="whitespace-pre flex-1">{line || " "}</span>
                </div>
              ))}
            </div>

            <div className={`absolute inset-0 transition-opacity duration-700 ${phase > 0 ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
              {phase === 1 && (
                 <div className="w-full h-full flex items-center justify-center flex-col gap-4">
                    <Loader2 size={18} className="text-white/20 animate-spin" />
                    <span className="text-[11px] font-mono text-white/30 tracking-wide uppercase">agent-1 is writing</span>
                 </div>
              )}
              {phase === 2 && [
                "// validation.ts",
                "",
                "export function validateEmail(email) {",
                '  const re = /^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$/;',
                "  if (!email) return 'Required';",
                "  if (!re.test(email)) return 'Invalid';",
                "  return null;",
                "}",
                "",
                "export function validatePassword(pwd) {",
                "  if (pwd.length < 8) return 'Too short';",
                "  if (!/\\d/.test(pwd)) return 'Need digit';",
                "  return null;",
                "}",
              ].map((line, i) => (
                <div key={i} className="flex px-4 opacity-0 animate-[fade-in_0.3s_forwards]" style={{ animationDelay: `${i * 30}ms` }}>
                  <span className="w-8 text-right pr-4 text-white/10 select-none">
                    {i + 1}
                  </span>
                  <span className="whitespace-pre flex-1 font-mono text-white/40">{line || " "}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Terminal */}
          <div className="h-[68px] border-t border-white/[0.05] bg-[#09090b] text-[11px] font-mono leading-relaxed text-white/40 overflow-hidden relative">
            <div className={`absolute inset-0 p-3 transition-all duration-700 ${phase === 2 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              <div className="text-white/50">
                $ bun test validation.test.ts
              </div>
              <div>&nbsp; ✓ rejects empty email</div>
              <div>&nbsp; ✓ rejects invalid format</div>
              <div className="text-white/60 transition-colors duration-500">&nbsp; 6 pass | 0 fail</div>
            </div>
          </div>
        </div>
      </div>

      {/* Status bar */}
      <div className="flex items-center px-4 h-7 bg-[#09090b] border-t border-white/[0.05] text-[10px] font-mono text-white/30 gap-3">
        <span className={phase >= 2 ? "text-white/50" : ""}>$0.058</span>
        <span className={phase >= 1 ? "text-white/50" : ""}>18ms</span>
        <span className={phase >= 1 ? "text-white/50" : ""}>
          Cache <span className={`transition-colors duration-500 ${phase >= 1 ? "text-white/80" : "text-white/20"}`}>82%</span>
        </span>
        <span className="ml-auto">Claude 3.5 Sonnet</span>
        <div className={`w-1.5 h-1.5 rounded-full transition-colors duration-500 ${phase >= 1 ? "bg-white/60 shadow-[0_0_8px_rgba(255,255,255,0.4)]" : "bg-white/10"}`} />
      </div>

      <style>{`
        @keyframes fade-in {
          to {
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}
